import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import type { AssistantMessage } from '@accounting/shared';
import { PROVIDER_LABELS, useCalendars, useConnections } from '../../hooks/useAssistant';
import { useCreateEvent } from '../../hooks/useAssistantTasks';
import { EventForm } from '../../components/assistant/EventForm';
import {
  ATTENTION_LABELS,
  snoozePresets,
  useFeed,
  useMarkDone,
  useMessage,
  useReopen,
  useReply,
  useSetFlag,
  useSnooze,
  useExtractTask,
  useJunk,
  useMute,
  useAcceptInvite,
} from '../../hooks/useAssistantMail';
import { ApiError } from '../../lib/api';

export const Route = createFileRoute('/_auth/assistant/attention')({ component: AttentionPage });

type Tab = 'open' | 'snoozed' | 'done' | 'inbox';
const TAB_LABELS: Record<Tab, string> = { open: 'Needs you', snoozed: 'Snoozed', done: 'Done', inbox: 'All mail' };

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? `Request failed (${err.status})`;
  }
  return err instanceof Error ? err.message : String(err);
}

function when(iso: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }), hour: 'numeric', minute: '2-digit' });
}

function AttentionPage() {
  const [tab, setTab] = useState<Tab>('open');
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const connections = useConnections();
  const mailConnections = useMemo(() => (connections.data ?? []).filter((c) => c.capabilities.mail), [connections.data]);
  const visibleIds = useMemo(() => mailConnections.filter((c) => !hidden.has(c.id)).map((c) => c.id), [mailConnections, hidden]);
  const feed = useFeed(tab, visibleIds);

  const counts = { open: tab === 'open' ? feed.data?.length : undefined };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 1100 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Email</h1>
        <div className="pa-toolbar__views" role="tablist">
          {(['open', 'snoozed', 'done', 'inbox'] as Tab[]).map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} className={`btn-secondary${tab === t ? ' pa-view--active' : ''}`} onClick={() => { setTab(t); setOpenId(null); }}>
              {TAB_LABELS[t]}
              {t === 'open' && counts.open !== undefined ? ` · ${counts.open}` : ''}
            </button>
          ))}
        </div>
        <span className="muted" style={{ fontSize: 13 }}>
          {tab === 'inbox' ? 'Every inbox message from every connected account, last four weeks, newest first.' : 'Only mail that needs you. Everything else stays in your inboxes.'}
        </span>
      </header>

      {message && (
        <div className="card pa-banner pa-banner--err" role="alert">
          <span>{message}</span>
          <button type="button" className="link-button" onClick={() => setMessage(null)}>Dismiss</button>
        </div>
      )}

      {mailConnections.length > 1 && (
        <div className="pa-chips">
          {mailConnections.map((c) => {
            const off = hidden.has(c.id);
            return (
              <button key={c.id} type="button" className={`pa-chip${off ? ' pa-chip--off' : ''}`} aria-pressed={!off}
                onClick={() => setHidden((prev) => { const n = new Set(prev); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n; })}>
                <span className="pa-dot" style={{ background: c.color }} aria-hidden="true" />
                {c.label}
              </button>
            );
          })}
        </div>
      )}

      {connections.data && mailConnections.length === 0 && (
        <div className="card">
          <p style={{ margin: 0 }}>
            No mailbox is connected. <Link to="/assistant/connections">Connect Microsoft 365 or Gmail</Link> to build the feed.
          </p>
        </div>
      )}

      {feed.isLoading && <p className="muted">Loading…</p>}
      {feed.data && feed.data.length === 0 && mailConnections.length > 0 && (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            {tab === 'open' ? 'Nothing needs you right now.' : tab === 'snoozed' ? 'Nothing snoozed.' : tab === 'done' ? 'Nothing marked done yet.' : 'No mail synced yet — the first pull runs right after connecting; use Sync now on Connections to refresh.'}
          </p>
        </div>
      )}

      {feed.data && feed.data.length > 0 && (
        <div className="card pa-feed">
          {feed.data.map((m) => (
            <FeedRow key={m.id} m={m} tab={tab} expanded={openId === m.id} onToggle={() => setOpenId(openId === m.id ? null : m.id)} onError={setMessage} />
          ))}
        </div>
      )}
    </div>
  );
}

function FeedRow({ m, tab, expanded, onToggle, onError }: { m: AssistantMessage; tab: Tab; expanded: boolean; onToggle: () => void; onError: (s: string) => void }) {
  const done = useMarkDone();
  const reopen = useReopen();
  const snooze = useSnooze();
  const flag = useSetFlag();
  const junk = useJunk();
  const mute = useMute();
  const accept = useAcceptInvite();
  const busy = done.isPending || reopen.isPending || snooze.isPending || flag.isPending || junk.isPending || mute.isPending || accept.isPending;
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [junkOpen, setJunkOpen] = useState(false);
  const [muteOpen, setMuteOpen] = useState(false);
  const domain = m.fromEmail ? m.fromEmail.slice(m.fromEmail.indexOf('@')) : '';
  const onErr = (err: unknown) => onError(errorMessage(err));

  return (
    <div className={`pa-feed__item${expanded ? ' pa-feed__item--open' : ''}${m.isRead ? '' : ' pa-feed__item--unread'}`}>
      <button type="button" className="pa-feed__row" onClick={onToggle} aria-expanded={expanded}>
        <span className="tag pa-feed__acct" style={{ background: m.color }} title={m.connectionLabel}>{m.connectionLabel}</span>
        {m.attentionState !== 'none' ? (
          <span className={`tag pa-state pa-state--${m.attentionState}`}>{ATTENTION_LABELS[m.attentionState] ?? m.attentionState}</span>
        ) : (
          <span className="tag" style={{ opacity: 0.5 }}>mail</span>
        )}
        <span className="pa-feed__from">{m.folder === 'sent' ? `To ${m.toEmails[0] ?? ''}` : (m.fromName || m.fromEmail || '—')}</span>
        <span className="pa-feed__subject">
          {m.repliedAt && <span className="tag pa-state pa-state--replied" title={`You replied ${when(m.repliedAt)}`}>↩ Replied</span>}
          {m.subject || '(no subject)'}
        </span>
        <span className="pa-feed__preview muted">{m.bodyPreview ?? ''}</span>
        {m.hasAttachments && <span className="muted" title="Has attachments">📎</span>}
        <span className="pa-feed__when muted">{when(m.receivedAt)}</span>
      </button>
      <div className="pa-feed__actions">
        {m.hasInvite && m.folder === 'inbox' && m.localState !== 'done' && (
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            title="Accept the meeting and put it on this account's calendar"
            onClick={() =>
              accept.mutate(m.id, {
                onSuccess: (r) => onError(r.eventId ? `Added "${r.summary}" to ${r.calendarLabel}.` : `Accepted. It will show on ${r.calendarLabel}'s calendar within a few minutes.`),
                onError: onErr,
              })
            }
          >
            {accept.isPending ? 'Accepting…' : '📅 Accept invite'}
          </button>
        )}
        {(tab === 'open' || tab === 'snoozed' || (tab === 'inbox' && m.attentionState !== 'none' && m.localState !== 'done')) && (
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => done.mutate({ id: m.id }, { onError: onErr })}>Done</button>
        )}
        {tab === 'open' && (
          <div className="dropdown-wrap">
            <button type="button" className="btn-secondary" disabled={busy} onClick={() => setSnoozeOpen((s) => !s)} aria-haspopup="menu" aria-expanded={snoozeOpen}>Snooze ▾</button>
            {snoozeOpen && (
              <div className="dropdown-menu" role="menu">
                {snoozePresets().map((p) => (
                  <button key={p.label} type="button" role="menuitem" className="link-button" onClick={() => { setSnoozeOpen(false); snooze.mutate({ id: m.id, until: p.until.toISOString() }, { onError: onErr }); }}>
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {m.folder === 'inbox' && m.canReply && tab !== 'done' && (
          <div className="dropdown-wrap">
            <button type="button" className="btn-secondary" disabled={busy} onClick={() => setJunkOpen((s) => !s)} aria-haspopup="menu" aria-expanded={junkOpen} title="Move to Junk at the provider and stop showing this sender">
              Junk ▾
            </button>
            {junkOpen && (
              <div className="dropdown-menu" role="menu">
                <button type="button" role="menuitem" className="link-button" onClick={() => { setJunkOpen(false); junk.mutate({ id: m.id, scope: 'sender' }, { onError: onErr }); }}>
                  Junk, mute {m.fromEmail ?? 'this sender'}
                </button>
                {domain && domain.length > 1 && (
                  <button type="button" role="menuitem" className="link-button" onClick={() => { setJunkOpen(false); junk.mutate({ id: m.id, scope: 'domain' }, { onError: onErr }); }}>
                    Junk, mute everyone {domain}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {m.folder === 'inbox' && tab !== 'done' && m.fromEmail && (
          <div className="dropdown-wrap">
            <button type="button" className="btn-secondary" disabled={busy} onClick={() => setMuteOpen((s) => !s)} aria-haspopup="menu" aria-expanded={muteOpen} title="Keep the mail in the inbox, just never ask for attention again">
              Mute ▾
            </button>
            {muteOpen && (
              <div className="dropdown-menu" role="menu">
                <button type="button" role="menuitem" className="link-button" onClick={() => { setMuteOpen(false); mute.mutate({ id: m.id, scope: 'sender' }, { onError: onErr }); }}>
                  Mute {m.fromEmail}
                </button>
                {domain && domain.length > 1 && (
                  <button type="button" role="menuitem" className="link-button" onClick={() => { setMuteOpen(false); mute.mutate({ id: m.id, scope: 'domain' }, { onError: onErr }); }}>
                    Mute everyone {domain}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {(tab === 'snoozed' || tab === 'done') && (
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => reopen.mutate({ id: m.id }, { onError: onErr })}>Reopen</button>
        )}
        {m.canReply && (
          <button type="button" className="btn-secondary" disabled={busy} title={m.isFlagged ? 'Remove flag' : 'Flag at the provider'} onClick={() => flag.mutate({ id: m.id, flagged: !m.isFlagged }, { onError: onErr })}>
            {m.isFlagged ? '⚑ Unflag' : '⚐ Flag'}
          </button>
        )}
        {m.webLink && (
          <a href={m.webLink} target="_blank" rel="noopener noreferrer" className="btn-secondary">Open in {PROVIDER_LABELS[m.provider] ?? m.provider}</a>
        )}
      </div>
      {expanded && <MessageDetail m={m} onError={onError} />}
    </div>
  );
}

function MessageDetail({ m, onError }: { m: AssistantMessage; onError: (s: string) => void }) {
  const detail = useMessage(m.id);
  const reply = useReply();
  const extract = useExtractTask();
  const calendars = useCalendars();
  const createEvent = useCreateEvent();
  const [calOpen, setCalOpen] = useState(false);
  const [text, setText] = useState('');
  const [added, setAdded] = useState<Set<number | 'subject'>>(() => new Set());
  const isMine = m.folder === 'sent';
  const lift = (key: number | 'subject', input: { taskIndex?: number; title?: string; dueDate?: string | null }) =>
    extract.mutate({ id: m.id, ...input }, { onSuccess: () => setAdded((s) => new Set(s).add(key)), onError: (err) => onError(errorMessage(err)) });
  return (
    <div className="pa-feed__detail">
      {m.aiExtracted && (m.aiExtracted.dates.length > 0 || m.aiExtracted.tasks.length > 0) && (
        <div className="pa-ai">
          {m.aiExtracted && m.aiExtracted.dates.length > 0 && (
            <div className="pa-ai__row">
              <span className="muted">Dates:</span>
              {m.aiExtracted.dates.map((d, i) => (
                <span key={i} className="tag" title={d.label}>{d.iso}{d.label ? ` · ${d.label}` : ''}</span>
              ))}
            </div>
          )}
          {m.aiExtracted && m.aiExtracted.tasks.length > 0 && (
            <div className="pa-ai__row">
              <span className="muted">Asks:</span>
              {m.aiExtracted.tasks.map((t, i) => (
                <button key={i} type="button" className="btn-secondary pa-ai__task" disabled={extract.isPending || added.has(i)} onClick={() => lift(i, { taskIndex: i })} title={t.due ? `Due ${t.due}` : 'No date'}>
                  {added.has(i) ? '✓ ' : '+ '}{t.title}{t.due ? ` (${t.due})` : ''}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="pa-ai__row">
        <button type="button" className="link-button" disabled={extract.isPending || added.has('subject')} onClick={() => lift('subject', { title: m.subject || 'Follow up' })}>
          {added.has('subject') ? '✓ Task created' : '+ Make this a task'}
        </button>
        <button type="button" className="link-button" onClick={() => setCalOpen(true)}>+ Add to calendar</button>
      </div>
      {calOpen && (
        <EventForm
          calendars={calendars.data ?? []}
          initial={{ subject: m.subject || '(no subject)', body: `From ${m.fromName || m.fromEmail || ''}: ${detail.data?.bodyPreview ?? m.bodyPreview ?? ''}`.trim() }}
          busy={createEvent.isPending}
          error={createEvent.isError ? errorMessage(createEvent.error) : null}
          onCreate={(input) => createEvent.mutate(input, { onSuccess: () => { setCalOpen(false); onError('Event added.'); }, onError: (err) => onError(errorMessage(err)) })}
          onUpdate={() => undefined}
          onClose={() => setCalOpen(false)}
        />
      )}
      <div className="muted" style={{ fontSize: 13 }}>
        {isMine ? `You → ${m.toEmails.join(', ')}` : `${m.fromName ? `${m.fromName} <${m.fromEmail}>` : m.fromEmail} → ${m.toEmails.join(', ')}`}
        {m.ccEmails.length > 0 ? ` · cc ${m.ccEmails.join(', ')}` : ''}
        {m.attentionReasons.length > 0 ? ` · ${m.attentionReasons.join(', ')}` : ''}
        {m.snoozedUntil ? ` · snoozed until ${when(m.snoozedUntil)}` : ''}
        {m.repliedAt ? ` · you replied ${when(m.repliedAt)}` : ''}
      </div>
      {detail.isLoading && <p className="muted">Loading message…</p>}
      {detail.isError && <p className="field-error">{errorMessage(detail.error)}</p>}
      {detail.data && <pre className="pa-feed__body">{detail.data.bodyText || detail.data.bodyPreview || '(empty)'}</pre>}
      {m.canReply && !isMine && (
        <form
          className="pa-feed__reply"
          onSubmit={(e) => {
            e.preventDefault();
            if (!text.trim()) return;
            reply.mutate({ id: m.id, bodyText: text.trim(), replyAll: false }, { onSuccess: () => setText(''), onError: (err) => onError(errorMessage(err)) });
          }}
        >
          <textarea rows={4} placeholder={`Reply to ${m.fromName || m.fromEmail || 'sender'}…`} value={text} onChange={(e) => setText(e.target.value)} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {(m.toEmails.length + m.ccEmails.length) > 1 && (
              <button type="button" className="btn-secondary" disabled={reply.isPending || !text.trim()} onClick={() => reply.mutate({ id: m.id, bodyText: text.trim(), replyAll: true }, { onSuccess: () => setText(''), onError: (err) => onError(errorMessage(err)) })}>
                Reply all
              </button>
            )}
            <button type="submit" className="btn-primary" disabled={reply.isPending || !text.trim()}>
              {reply.isPending ? 'Sending…' : 'Reply'}
            </button>
          </div>
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>Sends from {m.connectionLabel} and marks this done.</p>
        </form>
      )}
    </div>
  );
}
